// Flame Cells.
//
// This product shares NOTHING behavioural with Natural Draft Burners. Where a list
// is empty or a submit step is false below, that is a DECLARATION, not an omission:
// the shared machinery reads it and does nothing, rather than falling back to
// Natural Draft's answer.
import type { ProductProfile } from "./profile";
import { buildFlameCellRouting } from "./flameCellRouting";

// EACH STD/LN CORE PIPE TAKES EXACTLY ONE Core ID/OD (Kevin, 2026-09-03: "for core type std
// large [it] can only be that 6-5/8 STD core id/od, for ln large it should only be 6-5/8 LN,
// for std small it can only be the 4-1/2 std and for ln small it can only be 4-1/2 ln").
//
// This table IS the rule -- the four hide rules below are generated from it, so the pairing
// is stated once. It is also the same pairing the "core type ..." operations already write
// as a default (applyDefault('CORE_ID_OD', ...), seq 6 in operations.json). If one moves,
// this must move with it: the operation decides what the field HOLDS and this decides what
// it OFFERS, and a disagreement shows up as a dropdown whose only option is not the value
// in it.
const CORE_SIZE_BY_TYPE: ReadonlyArray<readonly [size: string, coreType: string]> = [
  ["CORE_IOD_6_58", "CORE_LARGE_HAND"], // 6-5/8" STD  <- STD - Large Core Pipe
  ["CORE_IOD_6_58LN", "CORE_LARGE_LOW_NOISE"], // 6-5/8" LN   <- LN - Large Core Pipe
  ["CORE_IOD_4_12", "CORE_SMALL_HAND"], // 4-1/2" STD  <- STD - Small Core Pipe
  ["CORE_IOD_4_12LN", "CORE_SMALL_LOW_NOISE"], // 4-1/2" LN   <- LN - Small Core Pipe
];
/** The four core-pipe sizes, as opposed to the raw pipe/tube/rod stock the other twelve Core
 *  ID/OD options describe. */
const CORE_PIPE_SIZES = CORE_SIZE_BY_TYPE.map(([size]) => size);

// Parsed-rule expressions (util/rule.ts): __["Q/ANSWER"] is truthy while that option is
// selected. sel() ORs a set of them, so a rule reads as the list of answers that trigger it.
const sel = (question: string, codes: readonly string[]): string =>
  codes.map((c) => `(__["${question}/${c}"])`).join("||");

const CORE_PIPE_TYPES = CORE_SIZE_BY_TYPE.map(([, coreType]) => coreType);
const STOCK_CORE_TYPES = ["CORE_PIPE", "CORE_TUBE", "CORE_ROUND_ROD_MACHINED"];
const IS_CORE_PIPE = sel("CORE_TYPE", CORE_PIPE_TYPES);
const IS_STOCK_CORE = sel("CORE_TYPE", STOCK_CORE_TYPES);
// LARGE vs SMALL core, for the face plate that has to fit it. Read off CORE_TYPE rather than
// off the size in Core ID/OD: with the pairing above the two say the same thing, but the core
// TYPE is what the user picks first, so the face plate list is right immediately instead of
// one reactive pass later, when the default op has written the size.
const LARGE_CORE_TYPES = ["CORE_LARGE_HAND", "CORE_LARGE_LOW_NOISE"];
const SMALL_CORE_TYPES = ["CORE_SMALL_HAND", "CORE_SMALL_LOW_NOISE"];
const IS_LARGE_CORE = sel("CORE_TYPE", LARGE_CORE_TYPES);
const IS_SMALL_CORE = sel("CORE_TYPE", SMALL_CORE_TYPES);

export const FLAME_CELLS_PROFILE: ProductProfile = {
  slug: "flame-cells",

  // Routing is COMPUTED, not filtered from rulesets: routings.json is empty for this
  // product because its routing came from a NetSuite User Event script driven by a labor
  // model (rolling time as a function of die, material and OD). That script is being
  // deactivated, so this is now the only thing that builds a Flame Cell routing. See
  // flameCellRouting.ts for what was and was not ported.
  computeRouting: buildFlameCellRouting,

  // Nothing is manually hidden. In particular CORE_TYPE, CORE_PLUG_TYPE and
  // CORE_PROTRUDING are real, answerable fields here -- Natural Draft hides a
  // question that happens to share the CORE_TYPE code, and that must not reach this
  // configurator. The two dependents carry the hide rule
  // X(CORE_TYPE/CORE_ROUND_ROD_MACHINED,CORE_PIPE,CORE_TUBE), so they can only ever
  // appear once CORE_TYPE itself can be answered.
  hiddenQuestionCodes: new Set(),

  // The export ships a PRODUCT_SPECIFICATIONS group with no questions in it and --
  // unlike the empty groups in the Natural Draft export -- no hide rule either
  // (show_box true, collapsable false). It therefore rendered as a permanent box the
  // user could not fold away, holding Group.tsx's scaffold line. Suppressed here
  // rather than in the JSON, because scripts/extract-product.cjs regenerates that
  // folder and the edit would be lost on the next import.
  hiddenGroupCodesByTab: {
    MAIN: new Set(["PRODUCT_SPECIFICATIONS"]),
  },

  // What a finished cell BOM must contain. A family that is required but produced no line
  // means a part the configuration calls for has no item mapped -- a core plug for a core OD
  // that has none, say -- and the BOM is marked "!" rather than quietly shipping short.
  //
  // Fin stock and rivets are mapped for every reachable combination today, so in practice
  // they only fire if a row is deleted. The core and the plug are the two that genuinely
  // have gaps: no plug exists for most core ODs or for a carbon steel core, and round rod is
  // mapped at 1/2" only.
  expectedComponentFamilies: [
    {
      label: "fin stock",
      rowPrefixes: ["Fin Stock"],
      requiredWhen: '(__["CELL_WEIGHT/CELL_WEIGHT"]>0)',
    },
    {
      label: "core plug",
      rowPrefixes: ["Core Plug"],
      requiredWhen: '(__["CORE_PLUG_QTY/CORE_PLUG_QTY"]>0)',
    },
    {
      // One family, three shapes: a complete core pipe assembly, raw pipe/tube stock, or
      // continuous round rod. Any one of them satisfies "this cell has a core".
      label: "core pipe / stock / rod",
      rowPrefixes: ["Core Pipe", "Core Stock", "Core Rod"],
      requiredWhen: '_("CORE_TYPE/*")',
    },
    {
      // Kevin, 2026-09-08: "only use the ones that say full assembly" -- the BOM rows point
      // at the -FA items, never the -A ones.
      //
      // Required whenever a REAL plate is chosen. Six of the eighteen plate options have no
      // FP-* item at all (both 1" EAS Centered plates, and all four Oversized plates with a
      // feature), so those configurations mark the BOM rather than quietly shipping a cell
      // with no plate on it. The None plate resolves to CELL_FACE_NONE, which this rule
      // deliberately does not require.
      label: "face plate",
      rowPrefixes: ["Face Plate"],
      requiredWhen:
        '_("CELL_FACEPLATE/*")&&!(__["CELL_FACEPLATE/CELL_FACE_NONE"])',
    },
    {
      label: "pop rivets",
      rowPrefixes: ["Pop Rivet"],
      requiredWhen:
        '(__["RIVET_QTY/ALUM"]>0)||(__["RIVET_QTY/SS_STD"]>0)||(__["RIVET_QTY/SS_SHORT"]>0)',
    },
  ],


  // WHICH CORE ID/OD, AND WHICH FACE PLATE SIZE (Kevin, 2026-09-01 and 2026-09-03). Two rules,
  // and the second only ever applies where the first has already run: the Face Plate group is
  // shown only for the four STD/LN core-pipe types (its questions carry
  // X(CORE_TYPE/CORE_LARGE_HAND,CORE_LARGE_LOW_NOISE,CORE_SMALL_HAND,CORE_SMALL_LOW_NOISE)),
  // which is exactly when Core ID/OD is pinned to one core-pipe size.
  //
  //   core type STD/LN large or small   ->  Core ID/OD offers ONLY that type's own size,
  //                                         per CORE_SIZE_BY_TYPE above
  //   core type Pipe / Tube / Round Rod ->  Core ID/OD offers the other twelve
  //
  //   core type STD/LN LARGE            ->  Face Plate Size offers only the LARGE pair
  //   core type STD/LN SMALL            ->  Face Plate Size offers only the SMALL pair
  //
  // None is deliberately left visible on Face Plate Size. It is a real, deliberate answer
  // here -- see hardRequiredQuestionCodes below, where Size is required precisely so that
  // "no face plate" is stated rather than left blank -- so hiding it would make a cell with
  // no face plate unorderable. Kevin has been asked whether it should go too.
  //
  // The face plate pair is written as "hidden when the OTHER core is selected" rather than
  // "hidden unless mine is", so an unanswered core type still shows every face plate size
  // instead of collapsing the list to None.
  answerHideRules: [
    // One rule per core-pipe size: hidden while ANOTHER core-pipe type is selected, and while
    // any stock core type is. So each of the four types leaves exactly its own size offered.
    //
    // Deliberately not "hidden unless my type is selected": an UNANSWERED core type would then
    // hide all four, and a fresh configuration is briefly unanswered before the default op
    // writes one -- the list would be missing the value about to land in it.
    ...CORE_SIZE_BY_TYPE.map(([size, coreType]) => ({
      question: "CORE_ID_OD",
      answers: [size],
      hiddenWhen:
        sel(
          "CORE_TYPE",
          CORE_PIPE_TYPES.filter((t) => t !== coreType)
        ) + `||${IS_STOCK_CORE}`,
    })),
    // And the twelve stock sizes are hidden for any core-pipe type.
    {
      question: "CORE_ID_OD",
      answers: CORE_PIPE_SIZES,
      otherAnswers: true,
      hiddenWhen: IS_CORE_PIPE,
    },
    {
      question: "CELL_FACEPLATE_SIZE",
      answers: ["FP_SIZE_STD_LARGE", "FP_SIZE_OS_LARGE"],
      hiddenWhen: IS_SMALL_CORE,
    },
    {
      question: "CELL_FACEPLATE_SIZE",
      answers: ["FP_SIZE_STD_SMALL", "FP_SIZE_OS_SMALL"],
      hiddenWhen: IS_LARGE_CORE,
    },
    // PLUGGING THE BOTTOM NEEDS A PROTRUDING CORE. Kevin, 2026-09-17: "if the core is
    // protruding then showing top and bottom selections for plugs is fine. However if it is
    // not protruding then we should not show the bottom selections."
    //
    // Stated as "hidden unless protruding is YES" rather than "hidden when protruding is NO",
    // so an unanswered Core Protruding hides them too -- the option appears only once the
    // core is known to protrude. "Plug both sides" is deliberately left alone: it is not a
    // bottom selection, and Kevin named the bottom ones.
    //
    // Narrowing the widget is only half of it. A hidden option keeps its selection (§8.3 of
    // the build spec), and the plug type drives the BOM line and the key's plug character --
    // so the "Core plug count" operation also reconciles a stale bottom choice. Both halves
    // are needed: this one stops it being picked, that one stops it surviving.
    {
      question: "CORE_PLUG_TYPE",
      answers: ["CORE_PLUG_BOTTOM_NWELDED", "CORE_PLUG_BOTTOM_WELDED"],
      hiddenWhen: `!(${sel("CORE_PROTRUDING", ["CORE_PROTRUDING_YES"])})`,
    },
  ],
  optionalQuestionCodes: new Set(),
  optionalQuestionPairs: [],
  // A half-answered face plate may never be submitted (Kevin, 8/20). These three are the
  // Face Plate group, and each one only becomes required once it is actually SHOWN --
  // Peep Sight once a real Size is picked, Features once that peep sight is the 1" one --
  // so this demands exactly "whatever the user can currently see is filled in", never an
  // invisible field. The whole group hides on the core types that have no face plate, and a
  // hidden field cannot block (findBlockingUnanswered filters the visible traversal).
  //
  // Size is included deliberately: "None" is a real, deliberate choice here, so leaving it
  // blank is an unanswered question rather than "no face plate".
  hardRequiredQuestionCodes: new Set([
    "CELL_FACEPLATE_SIZE",
    "CELL_FACEPLATE_PEEPSIGHT",
    "CELL_FACEPLATE_FEATURES",
  ]),
  suffixNormalizedFields: new Set(),

  // No FAH concept: that is a Natural Draft burner configuration.
  fahSectionGroupCodes: new Set(),
  // No typeable dropdown here: every list this CPQ shows is short enough to pick from.
  comboBoxQuestionCodes: new Set(),
  // Nothing here may invent a NetSuite list value.
  createsAnswersFor: new Set(),
  // Nothing is auto-created here, and no question is restricted to whole numbers,
  // because this product has no typeable list at all.
  autoCreatesAnswersFor: new Set(),
  wholeNumberQuestionCodes: new Set(),
  unlistedSizeTextFallback: new Map(),
  // Cell OD tops out at 99.99" (Kevin, 2026-10-02: "the cell od cannot be over 99.99").
  // Operation 13 writes the OD into the part number in thousandths -- 42" is the 42000 in
  // FAD-42000-411CCX-XXXW -- so 100" would make that segment six digits.
  maxInches: new Map([["CELL_OD/CELL_OD_ANSWER", 99.99]]),

  // No entries because this product has no encoded mapping at all -- the 367-char
  // fixed-width encoder is Natural Draft's. Notably CELL_OD exists in BOTH exports,
  // so this is the difference between an explicit "not applicable" and silently
  // inheriting a Natural Draft exemption.
  mappingHideExemptCodes: new Set(),

  // Matches this product's ITCR itemid template, {ID_KEY/ID}. NOT Natural Draft's
  // HIDDEN_ID_KEY/ID_KEY -- reading that here returned an empty fingerprint, which
  // silently skipped the already-exists lookup and built a NEW item on every submit
  // of a configuration that already had one.
  // TWO-LEVEL BOM. Kevin, 2026-09-08: "a flame cell with a face plate will always have a
  // flame cell with no face plate in its bom." So a plated cell's BOM is two lines -- the
  // bare cell, and the FP-* plate -- and the fin stock, core, plug and rivets sit on the bare
  // cell one level down. 855 of 1000 sampled BOM lines in the account are built that way.
  //
  // Same test as the face-plate component family above: a REAL plate, not the None plate,
  // which resolves to CELL_FACE_NONE and leaves the cell flat.
  nestedChildWhen: '_("CELL_FACEPLATE/*")&&!(__["CELL_FACEPLATE/CELL_FACE_NONE"])',
  // The plate is fitted to the plated assembly, not to the bare cell inside it, so its row
  // stays up here. Everything else moves down.
  nestedKeepRowPrefixes: ["Face Plate"],
  // Every size option, because an answer map holds one key per option: selecting None
  // without clearing the others would leave the old size standing alongside it. The resolver
  // at seq 0.5 collapses size x peep sight x features, so clearing the SIZE is enough to
  // drive CELL_FACEPLATE to CELL_FACE_NONE -- a stale peep sight or feature cannot revive a
  // plate on its own.
  nestedChildOverlay: {
    "CELL_FACEPLATE_SIZE/FP_SIZE_NONE": true,
    "CELL_FACEPLATE_SIZE/FP_SIZE_STD_SMALL": null,
    "CELL_FACEPLATE_SIZE/FP_SIZE_STD_LARGE": null,
    "CELL_FACEPLATE_SIZE/FP_SIZE_OS_SMALL": null,
    "CELL_FACEPLATE_SIZE/FP_SIZE_OS_LARGE": null,
  },

  fingerprintAnswerKey: "ID_KEY/ID",

  submit: {
    // PERMANENT, per Kevin (2026-08-11): "flame cells will never get a serial tag".
    // Not a pending wire-up. It matters that this is explicit: resolveSerialTagItem
    // never fails -- it falls back to "Serial Tag - Flameco" so a lookup failure can
    // never block a Natural Draft submit -- so calling it here would not yield "no
    // tag", it would yield the WRONG tag, described from Natural Draft answer keys
    // this config does not have. And the write-back never removes a line, so that
    // line would have to be deleted from the order by hand.
    serialTag: false,
    // Flange Gasket / Ext Nipple / Main Burner / Nozzle are Natural Draft parts.
    additionalLines: false,
    // No price list for Flame Cells yet, so the line keeps the placeholder amount.
    // This one IS a pending wire-up rather than a product fact.
    listPricing: false,
    // customrecord_customer_requirements is a port of Natural Draft's
    // CREATE_CUSTOMER_REQUIREMENTS script and its field set is keyed to Natural
    // Draft questions. Leaving it on would write a real, near-empty record against
    // a live customer on every Flame Cell submit.
    customerRequirements: false,
    // The direct item-field writes are keyed by Natural Draft ANSWER CODES. Core
    // Type is the clearest case: the map holds NA / STD_LARGE_CORE_PIPE / TUBE /
    // PIPE / ROUND_ROD_MACHINED, while this product answers CORE_LARGE_LOW_NOISE /
    // CORE_PIPE / CORE_ROUND_ROD_MACHINED / ... -- so every lookup would miss.
    // Flame Cells needs its own answer-code-to-list-id map before any of these can
    // be written; see the note in SaveSubmitButton.
    ndbItemFields: false,
  },

  // Shipping Instructions (operation seq 014) computes the starbar note into the
  // hidden SHIP_STAR_BARS_2 field, which no ITCR maps anywhere -- so the text was
  // computed and thrown away. The write-back merges it onto the order body field.
  orderFields: [["SHIP_STAR_BARS_2/SHIP_STAR_BARS_2", "custbodyshipping_instructions"]],
};
